/**
 * KPI Guide
 *
 * Comprehensive KPI theory, best practices, and usage documentation.
 * Rendered as the "Guide" tab content in the KPI scorecard page.
 */

import {
  BookOpen,
  Target,
  TrendingUp,
  DollarSign,
  BarChart3,
  Lightbulb,
  Code2,
  Layout,
  CheckCircle2,
  XCircle,
  ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { KpiApiDocs } from './KpiApiDocs';

/* -------------------------------------------------------------------------- */
/*  Section: What are KPIs?                                                   */
/* -------------------------------------------------------------------------- */

function WhatAreKpis() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BookOpen className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          What are KPIs?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          KPIs are measurable numbers that tell your organization whether it is on track.
          They are not reports -- they are the <strong className="text-foreground">pulse of your business</strong>.
          A good KPI scorecard replaces <em>"How do you think we're doing?"</em> with{' '}
          <em>"Here's exactly how we're doing."</em>
        </p>
        <ul className="space-y-2 list-none pl-0">
          <li className="flex items-start gap-2">
            <Target className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
            <span>KPIs turn strategy into numbers you can act on</span>
          </li>
          <li className="flex items-start gap-2">
            <TrendingUp className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
            <span>
              They create numerical alignment -- start with the financial plan, derive required
              results (R2), and cascade to operational metrics
            </span>
          </li>
          <li className="flex items-start gap-2">
            <BarChart3 className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
            <span>
              Every KPI should have a clear Required Result (target) so you can compare actual vs plan
            </span>
          </li>
          <li className="flex items-start gap-2">
            <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
            <span>
              The goal is execution visibility: communicate, measure, follow up, problem-solve
            </span>
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: The EOS Scorecard                                                */
/* -------------------------------------------------------------------------- */

function EosScorecard() {
  const eosMapping = [
    { eos: '5-15 numbers', atek: 'Use categories + team filter' },
    { eos: 'Weekly cadence', atek: 'Set frequency to "Weekly"' },
    { eos: '13-week trailing', atek: 'Period selector shows multiple periods' },
    { eos: 'Everyone has a number', atek: 'Owner field assigns accountability' },
    { eos: 'On track / Off track', atek: 'Goal + orientation rule = auto status' },
    { eos: 'L10 review', atek: 'Review off-track KPIs weekly' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Layout className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          The EOS Scorecard
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <p>
          In the Entrepreneurial Operating System (EOS), the Scorecard is one of the six key
          components. It gives leadership a <strong className="text-foreground">weekly pulse</strong> on the business.
        </p>
        <div className="space-y-2">
          <p className="font-medium text-foreground">EOS Scorecard Principles</p>
          <ul className="space-y-1.5 list-disc pl-5">
            <li><strong>5-15 numbers:</strong> Every organization should track 5-15 numbers weekly</li>
            <li><strong>Activity-based, not results-based:</strong> Track the activities that drive results</li>
            <li><strong>Weekly cadence:</strong> Reviewed every week during the Level 10 (L10) meeting</li>
            <li><strong>Everyone has a number:</strong> Every person on the leadership team owns at least one KPI</li>
            <li><strong>13-week trailing view:</strong> Shows one quarter of history to spot trends</li>
            <li><strong>Goal orientation:</strong> "Are we on track or off track?" Green or red. That's it.</li>
          </ul>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">EOS Concept</TableHead>
                <TableHead className="text-xs">ATEK Implementation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eosMapping.map((row) => (
                <TableRow key={row.eos}>
                  <TableCell className="font-medium text-xs">{row.eos}</TableCell>
                  <TableCell className="text-xs">{row.atek}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: The 5 KPI Categories (Carpedia pentagon)                          */
/* -------------------------------------------------------------------------- */

function KpiCategories() {
  const categories = [
    {
      name: 'Cost',
      badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      what: 'Financial efficiency, spend per unit',
      examples: 'Cost per sensor deployed, labor cost per installation',
    },
    {
      name: 'Schedule',
      badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      what: 'Timeliness, throughput, capacity',
      examples: 'On-time delivery %, installation lead time',
    },
    {
      name: 'Quality',
      badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      what: 'Defects, accuracy, rework',
      examples: 'Calibration accuracy %, ticket reopen rate',
    },
    {
      name: 'Experience',
      badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      what: 'Customer & employee satisfaction',
      examples: 'NPS score, customer health score, employee retention',
    },
    {
      name: 'Safety',
      badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      what: 'Incidents, compliance, wellness',
      examples: 'Incident rate (industry-specific)',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          The 5 KPI Categories
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Metrics should come from 4-5 balanced categories to ensure improving one area
          doesn't hurt another (Carpedia pentagon).
        </p>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Category</TableHead>
                <TableHead className="text-xs">What it measures</TableHead>
                <TableHead className="text-xs">ATEK examples</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat.name}>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cat.badge}`}>
                      {cat.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{cat.what}</TableCell>
                  <TableCell className="text-xs">{cat.examples}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: Leading vs Lagging Indicators                                     */
/* -------------------------------------------------------------------------- */

function LeadingVsLagging() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          Leading vs Lagging Indicators
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border p-3 space-y-1.5">
            <p className="font-medium text-foreground">Lagging Indicators</p>
            <p className="text-xs">Look backward -- confirm what already happened.</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              <Badge variant="secondary" className="text-xs">Revenue</Badge>
              <Badge variant="secondary" className="text-xs">Profit margin</Badge>
              <Badge variant="secondary" className="text-xs">Churn rate</Badge>
            </div>
          </div>
          <div className="rounded-lg border p-3 space-y-1.5">
            <p className="font-medium text-foreground">Leading Indicators</p>
            <p className="text-xs">Look forward -- predict what will happen.</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              <Badge variant="secondary" className="text-xs">Proposals sent</Badge>
              <Badge variant="secondary" className="text-xs">Meetings booked</Badge>
              <Badge variant="secondary" className="text-xs">Pipeline value</Badge>
            </div>
          </div>
        </div>
        <p>
          The most powerful scorecards track <strong className="text-foreground">leading indicators</strong> because
          they give you time to act.
        </p>
        <p className="italic">
          Rule: "If you want to improve X (lagging), you should do Y (leading)."
        </p>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: The Rule of 1 (Carpedia)                                         */
/* -------------------------------------------------------------------------- */

function RuleOfOne() {
  const rows = [
    { metric: 'Labor', annual: '$1,000,000', onePercent: '$10,000', improvement: '10%', impact: '$100,000' },
    { metric: 'Material Cost', annual: '$500,000', onePercent: '$5,000', improvement: '5%', impact: '$25,000' },
    { metric: 'Sales', annual: '$5,000,000', onePercent: '$50,000', improvement: '15%', impact: '$750,000 x margin' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
          The Rule of 1
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Before creating a KPI, ask: <strong className="text-foreground">"What is 1% better worth?"</strong> (Carpedia)
        </p>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Metric</TableHead>
                <TableHead className="text-xs text-right">Annual Spend</TableHead>
                <TableHead className="text-xs text-right">1% =</TableHead>
                <TableHead className="text-xs text-right">Realistic Improvement</TableHead>
                <TableHead className="text-xs text-right">Dollar Impact</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.metric}>
                  <TableCell className="font-medium text-xs">{row.metric}</TableCell>
                  <TableCell className="text-xs text-right">{row.annual}</TableCell>
                  <TableCell className="text-xs text-right">{row.onePercent}</TableCell>
                  <TableCell className="text-xs text-right">{row.improvement}</TableCell>
                  <TableCell className="text-xs text-right font-medium">{row.impact}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p>Track metrics where small improvements have the biggest dollar impact.</p>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: The KPI Culture Matrix (Carpedia 2x2)                            */
/* -------------------------------------------------------------------------- */

function CultureMatrix() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-rose-600 dark:text-rose-400" />
          The KPI Culture Matrix
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>A 2x2 grid: Culture vs KPI Evolution (Carpedia)</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {/* top-left */}
          <div className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-1">
            <p className="font-semibold text-amber-700 dark:text-amber-400">PLODDING</p>
            <p className="text-muted-foreground">
              High Culture + Low KPI -- good people, intuition-driven. Will improve slowly but never maximize.
            </p>
          </div>
          {/* top-right */}
          <div className="rounded-lg border-2 border-emerald-400 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 p-3 space-y-1">
            <p className="font-semibold text-emerald-700 dark:text-emerald-400">PEAK PERFORMANCE</p>
            <p className="text-muted-foreground">
              High Culture + High KPI -- the goal state. Data-informed people who execute with discipline.
            </p>
          </div>
          {/* bottom-left */}
          <div className="rounded-lg border-2 border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-950/30 p-3 space-y-1">
            <p className="font-semibold text-red-700 dark:text-red-400">PERIL</p>
            <p className="text-muted-foreground">
              Low Culture + Low KPI -- high risk of failure. No data, no discipline.
            </p>
          </div>
          {/* bottom-right */}
          <div className="rounded-lg border-2 border-zinc-400 dark:border-zinc-600 bg-zinc-100 dark:bg-zinc-900/50 p-3 space-y-1">
            <p className="font-semibold text-zinc-700 dark:text-zinc-300">PARALYSIS</p>
            <p className="text-muted-foreground">
              Low Culture + High KPI -- data overload, failure to execute. Metrics without meaning.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs pt-1">
          <span className="text-muted-foreground">Y-axis: Culture</span>
          <span className="text-muted-foreground">X-axis: KPI Evolution</span>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: How to Use the Scorecard                                         */
/* -------------------------------------------------------------------------- */

function HowToUse() {
  const actions = [
    { action: 'Enter values', desc: 'Click any editable cell, type a number, press Enter' },
    { action: 'Navigate', desc: 'Arrow keys, Tab / Shift+Tab between cells' },
    { action: 'Edit a KPI', desc: 'Click the KPI name -- side panel opens' },
    { action: 'Create a KPI', desc: 'Click "New KPI" button (admin / director only)' },
    { action: 'Switch periods', desc: 'Use the period dropdown' },
    { action: 'Filter by team', desc: 'Select a team from the dropdown' },
    { action: 'Reorder', desc: 'Drag the grip handle within a category' },
    { action: 'Export', desc: 'Click the download icon for CSV' },
    { action: 'Past periods', desc: 'Locked by default -- click the lock icon to unlock' },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lightbulb className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          How to Use the Scorecard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {actions.map((item) => (
            <div key={item.action} className="rounded-lg border p-3">
              <p className="text-sm font-medium text-foreground">{item.action}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: Best Practices                                                    */
/* -------------------------------------------------------------------------- */

function BestPractices() {
  const dos = [
    'One owner per KPI',
    'Assign KPIs to teams',
    'Set goals for every KPI',
    'Review weekly in L10',
    'Balance across 5 categories',
    'Keep 5-15 per team',
    'Include both leading and lagging',
    'Apply the Rule of 1',
    'Communicate the "Why"',
  ];

  const donts = [
    'Track more than 15 KPIs',
    'Create KPIs without goals',
    'Assign multiple owners',
    'Use KPIs for projects (use Rocks)',
    'Change KPIs mid-quarter',
    'Enter values late',
    'Track only lagging indicators',
    'Delegate strategic levers',
    'Create metrics nobody acts on',
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          Best Practices
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">DO</p>
            <ul className="space-y-1.5">
              {dos.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">DON'T</p>
            <ul className="space-y-1.5">
              {donts.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: KPI vs Rock vs To-Do                                              */
/* -------------------------------------------------------------------------- */

function KpiVsRockVsTodo() {
  const rows = [
    {
      type: 'KPI',
      nature: 'Ongoing measurable',
      cadence: 'Weekly / Monthly',
      example: 'Active sensors, churn rate',
    },
    {
      type: 'Rock',
      nature: '90-day project',
      cadence: 'Quarterly',
      example: 'Launch new dashboard, migrate database',
    },
    {
      type: 'To-Do',
      nature: 'Action item',
      cadence: 'Immediate (1-2 weeks)',
      example: 'Call customer, fix bug, schedule meeting',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="h-4 w-4 text-violet-600 dark:text-violet-400" />
          KPI vs Rock vs To-Do
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Nature</TableHead>
                <TableHead className="text-xs">Cadence</TableHead>
                <TableHead className="text-xs">Example</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.type}>
                  <TableCell>
                    <Badge variant="outline" className="text-xs font-medium">
                      {row.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{row.nature}</TableCell>
                  <TableCell className="text-xs">{row.cadence}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{row.example}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section: API Integration (collapsible)                                     */
/* -------------------------------------------------------------------------- */

function ApiSection() {
  return (
    <details className="group">
      <summary className="flex items-center justify-between cursor-pointer rounded-lg border bg-card px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors list-none [&::-webkit-details-marker]:hidden">
        <span className="flex items-center gap-2">
          <Code2 className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
          API Integration
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="mt-3">
        <KpiApiDocs />
      </div>
    </details>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                             */
/* -------------------------------------------------------------------------- */

export function KpiGuide() {
  return (
    <div className="space-y-6 pb-8">
      {/* Theory cards -- 2-column grid on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WhatAreKpis />
        <EosScorecard />
        <KpiCategories />
        <LeadingVsLagging />
        <RuleOfOne />
        <CultureMatrix />
      </div>

      {/* Practical sections -- full-width */}
      <HowToUse />
      <BestPractices />
      <KpiVsRockVsTodo />

      {/* API section -- collapsed by default */}
      <ApiSection />
    </div>
  );
}
